import { LinkedinLogo } from "@phosphor-icons/react";
import clsx from "clsx";
import { useState } from "react";

import RelativeLink from "common/components/RelativeLink";
import { Button } from "common/components/ui/Button";
import {
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  DrawerBody,
  DrawerClose,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import {
  typedObjectEntries,
  typeSafeObjectFromEntries,
} from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { LEAD_LIST_SOURCE_TYPES } from "../../constants";
import LinkedInImport from "./LinkedInImport";

const SOURCE_CATEGORIES_KEYS = ["list", "drip", "crm"] as const;
const CATEGORIES = {
  linkedin: {
    icon: LinkedinLogo,
    label: "Import from LinkedIn",
    subtitle: "Search, Sales Navigator, Posts, Events, Groups",
    iconContainerClassName: "text-blue-500 border-blue-200 bg-blue-50",
  },
  ...typeSafeObjectFromEntries(
    SOURCE_CATEGORIES_KEYS.map((key) => {
      const { icon, label, subtitle, iconContainerClassName } =
        LEAD_LIST_SOURCE_TYPES[key];
      return [key, { icon, label, subtitle, iconContainerClassName }] as const;
    }),
  ),
} as const;

interface AddSourceFlowProps {
  listId: string;
}

export default function AddSourceFlow({ listId }: AddSourceFlowProps) {
  const isDesktop = useTwBreakpoint("lg");

  const [selectedCategory, setSelectedCategory] =
    useState<keyof typeof CATEGORIES>();
  const [isNextStep, setIsNextStep] = useState(false);

  function onBack() {
    setIsNextStep(false);
    setSelectedCategory(undefined);
  }

  const Body = isDesktop ? DialogBody : DrawerBody;

  const mainComponent = isNextStep ? (
    <LinkedInImport listId={listId} goBack={onBack} />
  ) : (
    <Body>
      <div className="flex flex-col lg:grid lg:grid-cols-2 gap-2">
        {typedObjectEntries(CATEGORIES).map(
          ([key, { label, subtitle, icon: Icon, iconContainerClassName }]) => {
            return (
              <button
                onClick={() => {
                  setSelectedCategory(key);
                }}
                type="button"
                className={clsx(
                  "p-5 flex items-center gap-x-5 gap-y-1 bg-black-50 border-black-50 rounded-5xl border",
                  key === selectedCategory
                    ? "border-purple-500"
                    : "border-transparent hover:bg-black-100 hover:border-black-100",
                )}
                key={key}
              >
                <div
                  className={clsx(
                    "size-12 rounded-2xl flex items-center justify-center shrink-0 border",
                    iconContainerClassName,
                  )}
                >
                  <Icon className="size-5" />
                </div>
                <div className="flex flex-col gap-y-1">
                  <span className="text-xs-bold text-left">{label}</span>
                  <span className="text-xxs text-black-400 text-left">
                    {subtitle}
                  </span>
                </div>
              </button>
            );
          },
        )}
      </div>
    </Body>
  );

  const Header = isDesktop ? DialogHeader : DrawerHeader;
  const Close = isDesktop ? DialogClose : DrawerClose;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;

  return (
    <>
      {/* Header */}
      {!isNextStep && <Header title="Add leads" />}
      {mainComponent}
      {!isNextStep && (
        <Footer className="justify-between">
          <Close asChild>
            <Button variant="secondary-black">Cancel</Button>
          </Close>
          {selectedCategory === "linkedin" ? (
            <Button
              onClick={() => setIsNextStep(true)}
              disabled={!selectedCategory}
            >
              Next
            </Button>
          ) : (
            <Button asChild>
              <RelativeLink
                to={`lists/${listId}/sources/new/${selectedCategory}`}
              >
                Next
              </RelativeLink>
            </Button>
          )}
        </Footer>
      )}
    </>
  );
}
